import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import {
  BuyBoxApiError,
  adicionarMarca,
  dispararAtualizacaoStatus,
  fetchBuyBoxAnalise,
  fetchBuyBoxSnapshot,
  fetchBuyBoxStatus,
  fetchMarcas,
  fetchRevendedoresVerificados,
  marcarRevendedorVerificado,
  removerMarca,
  type BuyBoxStatus,
} from './buybox/buyboxService'
import {
  agruparDenuncias,
  agruparPorSecoes,
  formatarDataHora,
  formatarMargemTexto,
  formatarMoeda,
} from './buybox/logic'
import type { MarcasClassificacao, RevendedorVerificado, SnapshotBuyBox } from './buybox/types'

const CONTAS = [
  { id: '777058595', nome: 'Matriz (FIESTAPARTYFESTA)' },
  { id: '3131364517', nome: 'Extrema (FIESTAPARTYHOME)' },
]

const KPI_DEF: Array<{ key: keyof SnapshotBuyBox['resumo']; label: string; cor: string }> = [
  { key: 'ganhando', label: 'Ganhando', cor: '#15803d' },
  { key: 'perdendo', label: 'Perdendo', cor: '#c2410c' },
  { key: 'empate', label: 'Empate', cor: '#0e7490' },
  { key: 'nao_participante', label: 'Não participante', cor: '#64748b' },
  { key: 'erro', label: 'Erro', cor: '#b91c1c' },
]

const POLL_MS = 4000

/** Modal "Gerenciar marcas": as duas listas de classificação + a tabela de revendedores
 * verificados, com um cadastro direto (sem precisar esperar o vendedor aparecer numa denúncia). */
function ModalGestaoMarcas({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [marcas, setMarcas] = useState<MarcasClassificacao | null>(null)
  const [verificados, setVerificados] = useState<RevendedorVerificado[]>([])
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [novaExclusiva, setNovaExclusiva] = useState('')
  const [novaGrupo, setNovaGrupo] = useState('')
  const [mostrarNovoVerificado, setMostrarNovoVerificado] = useState(false)
  const [novoSellerId, setNovoSellerId] = useState('')
  const [novoNome, setNovoNome] = useState('')
  const [novoCnpj, setNovoCnpj] = useState('')
  const [novoNf, setNovoNf] = useState('')

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const [m, v] = await Promise.all([fetchMarcas(), fetchRevendedoresVerificados()])
      setMarcas(m)
      setVerificados(v)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar marcas.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    if (open) carregar()
  }, [open, carregar])

  const adicionar = async (marcaRaw: string, classificacao: 'exclusiva' | 'grupo', limpar: () => void) => {
    const valor = marcaRaw.trim()
    if (!valor) return
    try {
      await adicionarMarca(valor, classificacao)
      limpar()
      carregar()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao adicionar marca.')
    }
  }

  const remover = async (marcaRaw: string) => {
    try {
      await removerMarca(marcaRaw)
      carregar()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao remover marca.')
    }
  }

  const salvarNovoVerificado = async () => {
    if (!novoSellerId.trim()) return
    try {
      await marcarRevendedorVerificado({
        seller_id: novoSellerId.trim(),
        nome_vendedor: novoNome.trim(),
        cnpj: novoCnpj.trim(),
        nota_fiscal: novoNf.trim(),
      })
      setMostrarNovoVerificado(false)
      setNovoSellerId('')
      setNovoNome('')
      setNovoCnpj('')
      setNovoNf('')
      carregar()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao marcar revendedor verificado.')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700 }}>Gestão de marcas</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cada grafia exata da marca (como aparece na API) é uma entrada própria — o Mercado Livre não normaliza
          acentos.
        </Typography>
        {erro ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
            {erro}
          </Alert>
        ) : null}
        {carregando && !marcas ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <Box>
                <Typography
                  sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: 13, color: '#b91c1c', mb: 1 }}
                >
                  Marcas exclusivas Fiesta
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                  {(marcas?.exclusivas ?? []).map((m) => (
                    <Chip key={m} label={m} onDelete={() => remover(m)} size="small" />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    placeholder="Grafia exata da marca..."
                    fullWidth
                    value={novaExclusiva}
                    onChange={(e) => setNovaExclusiva(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => adicionar(novaExclusiva, 'exclusiva', () => setNovaExclusiva(''))}
                  >
                    Adicionar
                  </Button>
                </Stack>
              </Box>
              <Box>
                <Typography
                  sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: 13, color: '#0e7490', mb: 1 }}
                >
                  Marcas grupo Bellocopo
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                  {(marcas?.grupo ?? []).map((m) => (
                    <Chip key={m} label={m} onDelete={() => remover(m)} size="small" />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    placeholder="Grafia exata da marca..."
                    fullWidth
                    value={novaGrupo}
                    onChange={(e) => setNovaGrupo(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => adicionar(novaGrupo, 'grupo', () => setNovaGrupo(''))}
                  >
                    Adicionar
                  </Button>
                </Stack>
              </Box>
            </Box>

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(15,23,42,0.08)' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: 13 }}>
                  Revendedores verificados — grupo Bellocopo ({verificados.length})
                </Typography>
                <Button size="small" variant="outlined" onClick={() => setMostrarNovoVerificado((v) => !v)}>
                  + Adicionar revendedor verificado
                </Button>
              </Stack>

              {mostrarNovoVerificado ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.5,
                    p: 2,
                    mb: 2,
                    bgcolor: 'rgba(15,23,42,0.03)',
                    borderRadius: 2,
                  }}
                >
                  <TextField
                    size="small"
                    label="Seller ID"
                    value={novoSellerId}
                    onChange={(e) => setNovoSellerId(e.target.value)}
                  />
                  <TextField
                    size="small"
                    label="Nome do vendedor"
                    value={novoNome}
                    onChange={(e) => setNovoNome(e.target.value)}
                  />
                  <TextField size="small" label="CNPJ" value={novoCnpj} onChange={(e) => setNovoCnpj(e.target.value)} />
                  <TextField
                    size="small"
                    label="Número da NF"
                    value={novoNf}
                    onChange={(e) => setNovoNf(e.target.value)}
                  />
                  <Stack direction="row" spacing={1} sx={{ gridColumn: '1 / -1', justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setMostrarNovoVerificado(false)}>
                      Cancelar
                    </Button>
                    <Button size="small" variant="contained" onClick={salvarNovoVerificado}>
                      Salvar
                    </Button>
                  </Stack>
                </Box>
              ) : null}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vendedor</TableCell>
                    <TableCell>Seller ID</TableCell>
                    <TableCell>CNPJ</TableCell>
                    <TableCell>NF</TableCell>
                    <TableCell>Verificado em</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {verificados.map((v) => (
                    <TableRow key={v.seller_id}>
                      <TableCell>{v.nome_vendedor}</TableCell>
                      <TableCell>{v.seller_id}</TableCell>
                      <TableCell>{v.cnpj}</TableCell>
                      <TableCell>{v.nota_fiscal}</TableCell>
                      <TableCell>{formatarDataHora(v.verificado_em)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Modal "Marcar como revendedor verificado", aberto a partir de uma linha da seção Bellocopo. */
function ModalVerificarVendedor({
  vendedor,
  onClose,
  onSalvo,
}: {
  vendedor: { seller_id: string; nome_vendedor: string } | null
  onClose: () => void
  onSalvo: () => void
}) {
  const [cnpj, setCnpj] = useState('')
  const [nf, setNf] = useState('')
  const [observacao, setObservacao] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    setCnpj('')
    setNf('')
    setObservacao('')
    setErro(null)
  }, [vendedor])

  const salvar = async () => {
    if (!vendedor) return
    setSalvando(true)
    setErro(null)
    try {
      await marcarRevendedorVerificado({
        seller_id: vendedor.seller_id,
        nome_vendedor: vendedor.nome_vendedor,
        cnpj,
        nota_fiscal: nf,
        observacao: observacao || undefined,
      })
      onSalvo()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar verificação.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={Boolean(vendedor)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700 }}>
        Marcar como revendedor verificado
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Vale para o grupo Bellocopo inteiro — comprovando esta compra, o vendedor fica liberado nas outras marcas
          do grupo também.
        </Typography>
        {erro ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        ) : null}
        <Stack spacing={2}>
          <TextField size="small" label="Vendedor" value={vendedor?.nome_vendedor ?? ''} disabled />
          <TextField
            size="small"
            label="CNPJ"
            required
            placeholder="00.000.000/0000-00"
            value={cnpj}
            onChange={(e) => setCnpj(e.target.value)}
          />
          <TextField
            size="small"
            label="Número da NF"
            required
            placeholder="000000000"
            value={nf}
            onChange={(e) => setNf(e.target.value)}
          />
          <TextField
            size="small"
            label="Observação (opcional)"
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={salvando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={salvar} disabled={salvando || !cnpj || !nf}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function BuyBoxPage() {
  const [contaId, setContaId] = useState(CONTAS[0].id)
  const [snapshot, setSnapshot] = useState<SnapshotBuyBox | null>(null)
  const [status, setStatus] = useState<BuyBoxStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({ loading: false })
  const [modalMarcasAberto, setModalMarcasAberto] = useState(false)
  const [vendedorParaVerificar, setVendedorParaVerificar] = useState<{
    seller_id: string
    nome_vendedor: string
  } | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const carregar = useCallback(async (conta: string) => {
    setLoading(true)
    setError(null)
    setSnapshot(null)
    setStatus(null)
    setLeituraIa({ loading: false })
    try {
      const [dados, st] = await Promise.all([
        fetchBuyBoxSnapshot(conta).catch((e) => {
          // sem coleta salva ainda pra essa conta — não é erro fatal, a tela orienta a atualizar
          if (e instanceof BuyBoxApiError && e.status === 503) return null
          throw e
        }),
        fetchBuyBoxStatus(conta),
      ])
      setSnapshot(dados)
      setStatus(st)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao carregar Buy Box.')
    } finally {
      setLoading(false)
    }
  }, [])

  const carregarLeitura = useCallback(async (conta: string) => {
    setLeituraIa((prev) => ({ ...prev, loading: true, erro: null }))
    const data = await fetchBuyBoxAnalise(conta)
    if (!data.ok) {
      setLeituraIa({
        loading: false,
        erro: data.erro || data.message || data.error || 'Leitura IA indisponível.',
      })
      return
    }
    setLeituraIa({
      loading: false,
      texto: data.texto || data.leitura,
      modelo: data.modelo,
      fonte: data.fonte,
      cached: data.cached,
      gerado_em: data.gerado_em,
    })
  }, [])

  useEffect(() => {
    carregar(contaId)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [contaId, carregar])

  useEffect(() => {
    if (!snapshot) return
    carregarLeitura(contaId)
  }, [snapshot, contaId, carregarLeitura])

  const dispararEAcompanhar = async (acao: () => Promise<{ ok: true }>) => {
    try {
      await acao()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao disparar a atualização.')
      return
    }
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      const st = await fetchBuyBoxStatus(contaId).catch(() => null)
      if (!st) return
      setStatus(st)
      if (!st.atualizando && !st.rescaneando) {
        if (pollRef.current) clearInterval(pollRef.current)
        carregar(contaId)
      }
    }, POLL_MS)
  }

  const contaNome = CONTAS.find((c) => c.id === contaId)?.nome ?? contaId
  const secoesDenuncia = snapshot ? agruparDenuncias(snapshot.itens) : []
  const secoesPreco = snapshot ? agruparPorSecoes(snapshot.itens) : []
  const emAndamento = Boolean(status?.atualizando || status?.rescaneando)

  return (
    <DomainPageShell
      title="Buy Box"
      subtitle={
        status?.ultima_atualizacao
          ? `Status de Buy Box do catálogo · atualizado em ${formatarDataHora(status.ultima_atualizacao)}${
              snapshot ? ` · ${snapshot.itens.length} itens catalogados` : ''
            }`
          : 'Status de Buy Box do catálogo'
      }
      loading={loading}
      error={error}
      onRetry={() => carregar(contaId)}
    >
      <Stack spacing={2.5}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Tabs value={contaId} onChange={(_, v) => setContaId(v)} sx={{ minHeight: 36 }}>
                {CONTAS.map((c) => (
                  <Tab key={c.id} value={c.id} label={c.nome.split(' (')[0]} sx={{ minHeight: 36, py: 0.5 }} />
                ))}
              </Tabs>
              <Typography variant="body2" color="text.secondary">
                {contaNome}
                {status?.ultimo_rescan_catalogo
                  ? ` · catálogo verificado em ${formatarDataHora(status.ultimo_rescan_catalogo)}`
                  : ''}
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button variant="outlined" onClick={() => setModalMarcasAberto(true)}>
                  Gerenciar marcas
                </Button>
                <Button
                  variant="contained"
                  disabled={emAndamento}
                  startIcon={emAndamento ? <CircularProgress size={14} color="inherit" /> : undefined}
                  onClick={() => dispararEAcompanhar(() => dispararAtualizacaoStatus(contaId))}
                >
                  {emAndamento ? 'Atualizando…' : 'Atualizar'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {status?.ultimo_erro ? <Alert severity="warning">{status.ultimo_erro}</Alert> : null}

        {!snapshot && !loading ? (
          <Alert severity="info">
            Ainda não há checagem de Buy Box salva para {contaNome}. Clique em "Atualizar".
          </Alert>
        ) : null}

        {snapshot ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 1.5 }}>
            {KPI_DEF.map((kpi) => (
              <Card key={kpi.key}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    {kpi.label}
                  </Typography>
                  <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', mt: 0.5, color: kpi.cor }}>
                    {snapshot.resumo[kpi.key]}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : null}

        {snapshot ? (
          <PropostaIaCard
            title="Leitura do Buy Box"
            caption="Reflecte os KPIs e as acções desta conta e aponta o que os números sozinhos não mostram. A verdade continua nas tabelas abaixo — revise antes de agir."
            state={leituraIa}
            onRetry={() => carregarLeitura(contaId)}
          />
        ) : null}

        {snapshot && secoesDenuncia.length === 0 && secoesPreco.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Nenhum item com ação pendente.
          </Typography>
        ) : null}

        <Stack spacing={2}>
          {secoesDenuncia.map((secao) => (
            <Card key={secao.titulo} sx={{ overflow: 'hidden' }}>
              <Box
                sx={{ bgcolor: secao.cor, color: '#fff', px: 2.25, py: 1.25, display: 'flex', gap: 1.5, alignItems: 'center' }}
              >
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: 14 }}>
                  {secao.titulo}
                </Typography>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 999, px: 1.1, fontSize: 12, fontWeight: 600 }}>
                  {secao.linhas.length}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2.25, pt: 1.25 }}>
                {secao.descricao}
              </Typography>
              <Box sx={{ overflowX: 'auto', px: 2.25, pb: 1.5, pt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>MLB</TableCell>
                      <TableCell>Título</TableCell>
                      <TableCell>Marca</TableCell>
                      <TableCell>Vendedor</TableCell>
                      {secao.linhas[0]?.categoria === 'denunciar_grupo_pedir_identificacao' ? (
                        <TableCell align="right" />
                      ) : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {secao.linhas.map((linha) => (
                      <TableRow key={linha.mlb + linha.seller_id} hover>
                        <TableCell>
                          {linha.permalink ? (
                            <a href={linha.permalink} target="_blank" rel="noopener noreferrer">
                              {linha.mlb}
                            </a>
                          ) : (
                            linha.mlb
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>{linha.titulo}</TableCell>
                        <TableCell>{linha.marca ?? '—'}</TableCell>
                        <TableCell>{linha.nome_vendedor}</TableCell>
                        {linha.categoria === 'denunciar_grupo_pedir_identificacao' ? (
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                setVendedorParaVerificar({
                                  seller_id: linha.seller_id,
                                  nome_vendedor: linha.nome_vendedor,
                                })
                              }
                            >
                              Marcar como verificado
                            </Button>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          ))}

          {secoesPreco.map((secao) => (
            <Card key={secao.titulo} sx={{ overflow: 'hidden' }}>
              <Box
                sx={{ bgcolor: secao.cor, color: '#fff', px: 2.25, py: 1.25, display: 'flex', gap: 1.5, alignItems: 'center' }}
              >
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: 14 }}>
                  {secao.titulo}
                </Typography>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 999, px: 1.1, fontSize: 12, fontWeight: 600 }}>
                  {secao.itens.length}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2.25, pt: 1.25 }}>
                {secao.descricao}
              </Typography>
              <Box sx={{ overflowX: 'auto', px: 2.25, pb: 1.5, pt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>MLB</TableCell>
                      <TableCell>Título</TableCell>
                      <TableCell>Marca</TableCell>
                      <TableCell>Vencedor</TableCell>
                      <TableCell align="right">Preço atual</TableCell>
                      <TableCell align="right">Preço alvo</TableCell>
                      <TableCell align="right">Margem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {secao.itens.map((item) => (
                      <TableRow key={item.mlb + secao.titulo} hover>
                        <TableCell>
                          {item.permalink ? (
                            <a href={item.permalink} target="_blank" rel="noopener noreferrer">
                              {item.mlb}
                            </a>
                          ) : (
                            item.mlb
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>{item.titulo}</TableCell>
                        <TableCell>{item.marca ?? '—'}</TableCell>
                        <TableCell>{item.vencedor?.nome_vendedor ?? '—'}</TableCell>
                        <TableCell align="right">{formatarMoeda(item.preco_atual)}</TableCell>
                        <TableCell align="right">{formatarMoeda(item.preco_para_vencer)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatarMargemTexto(item)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          ))}
        </Stack>
      </Stack>

      <ModalGestaoMarcas open={modalMarcasAberto} onClose={() => setModalMarcasAberto(false)} />
      <ModalVerificarVendedor
        vendedor={vendedorParaVerificar}
        onClose={() => setVendedorParaVerificar(null)}
        onSalvo={() => {
          setVendedorParaVerificar(null)
          carregar(contaId)
        }}
      />
    </DomainPageShell>
  )
}
